import { useTranslation } from 'react-i18next'
import Margin from 'components/Margin'
import { createCancelDialogContext } from 'components/Dispatch/Cancel/useCancelServiceDialog'
import { Label, Paragraph, Heading } from 'components/primitives'
import React, { useMemo } from 'react'
import { RetryLink } from 'pages/Ticket/routes/Default/DispatchEvents/DispatchUlyEventProxy'
import { useGetAllstateDispatches } from 'hooks/universalPayload'
import * as Ariakit from '@ariakit/react'
import CancelDispatch from './Cancel'
import { useSelector } from 'react-redux'
import { selectRecentDispatches } from 'redux/appStore'
import { allStateData, AllstateStatus } from 'types/allstate'
import { DynamoEvents } from 'types/events'
import { DispatchAllStateRideInfo } from './DispatchAllstateRideInfo'
import { ProviderNames } from 'hooks/dispatch/utils'

type Props = {
  dispatchId?: string | null
  disableCancel?: boolean
  event?: DynamoEvents
}

export const cancelDialogContext = createCancelDialogContext()

const CancellableAllstateStatus: AllstateStatus[] = [
  AllstateStatus.NEWJOB,
  AllstateStatus.BOOKED,
  AllstateStatus.ASSIGNED,
  AllstateStatus.ENROUTE,
  AllstateStatus.ENROUTEDISABLE,
]

export function getAllstateDispatchData(data: allStateData, dispatchId, recentDispatches): allStateData | undefined {
  const id = dispatchId ?? ''
  if (!id) return undefined

  const fromRedux = recentDispatches?.AllstateDispatches?.find((d) => (d?.serviceRequest?.callId ?? '') === id)
  if (fromRedux) return fromRedux

  // Fallback to the hook data if redux does not have it
  if (Array.isArray(data)) {
    return data.find((d) => String(d?.serviceRequest?.callId ?? '') === id)
  }
  return data
}

function DispatchAllstateService({ dispatchId, disableCancel, event }: Props) {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useGetAllstateDispatches(dispatchId)
  const { cancelled } = cancelDialogContext.useCancelDialogContext()
  const dialogStore = Ariakit.useDialogStore()
  const recentDispatches = useSelector(selectRecentDispatches)

  const dispatchData = useMemo(
    () => getAllstateDispatchData(data, dispatchId, recentDispatches),
    [data, dispatchId, recentDispatches],
  )

  const allstateStatus = dispatchData?.serviceRequest?.status || 'N/A'
  const canCancel = CancellableAllstateStatus.includes(allstateStatus as AllstateStatus)
  const isCancelled = allstateStatus === AllstateStatus.CANCELLED
  const isCompleted = allstateStatus === AllstateStatus.COMPLETED

  const eta: string =
    dispatchData?.serviceRequest?.eta?.etaMinMinutes != null && dispatchData?.serviceRequest?.eta?.etaMaxMinutes != null
      ? dispatchData?.serviceRequest?.eta?.etaMinMinutes === dispatchData?.serviceRequest?.eta?.etaMaxMinutes
        ? `${dispatchData?.serviceRequest?.eta?.etaMinMinutes} ${t('labels.mins')}`
        : `${dispatchData?.serviceRequest?.eta?.etaMinMinutes} ${t('labels.mins')} - ${dispatchData?.serviceRequest?.eta?.etaMaxMinutes} ${t('labels.mins')}`
      : 'N/A'

  if (error)
    return (
      <>
        <Heading as="h4">{t('universal.dispatchHeading')}</Heading>
        <Label>{t('Error Loading Details...')}</Label>
        <RetryLink
          onClick={async (event) => {
            const shouldFireQuery = true
            event.preventDefault()
            event.stopPropagation()
            await refetch(shouldFireQuery)
          }}
        >
          {t('Retry')}
        </RetryLink>
      </>
    )

  if (loading)
    return (
      <>
        <Heading as="h4">{t('universal.dispatchHeading')}</Heading>
        <Label>{t('Retrieving...')}</Label>
      </>
    )

  return (
    <>
      <Heading as="h4">{t('universal.dispatchHeading')}</Heading>
      <div style={{ marginLeft: 8 }}>
        <Margin spacing="none">
          {dispatchData && dispatchData?.serviceRequest && (
            <>
              <Margin spacing="md">
                <cancelDialogContext.Provider>
                  <Ariakit.DialogProvider store={dialogStore}>
                    <Label>{t('labels.dispatchVendor')}</Label>
                    <Paragraph spacing="sm">{t('headings.allstate')}</Paragraph>
                    <Margin spacing="sm">
                      <Label>{t('universal.dispatchId')}</Label>
                      <Paragraph spacing="sm">{dispatchData?.serviceRequest?.callId}</Paragraph>
                    </Margin>
                    <Label>{t('labels.status')}</Label>
                    <Paragraph style={{ textTransform: 'capitalize' }} spacing="sm">
                      {allstateStatus}
                    </Paragraph>
                    {!isCancelled && !isCompleted && !cancelled && !disableCancel && dispatchData && (
                      <>
                        <Label>{t('universal.service')}</Label>
                        <Margin spacing="sm">{`${dispatchData.serviceRequest?.services[0]?.name ?? 'N/A'}`}</Margin>
                        <Label>{t('labels.eta')}</Label>
                        <Margin spacing="md">{eta}</Margin>
                        <CancelDispatch
                          callKey={dispatchData?.serviceRequest?.callId as string}
                          provider={ProviderNames.ALLSTATE}
                          service={dispatchData?.serviceRequest}
                          cancelDisabled={!canCancel}
                        />
                      </>
                    )}
                  </Ariakit.DialogProvider>
                </cancelDialogContext.Provider>
              </Margin>
            </>
          )}
        </Margin>
        {event && <DispatchAllStateRideInfo event={event} />}
      </div>
    </>
  )
}

export default DispatchAllstateService
